import React from "react";
import { useReveal } from "@/hooks/useReveal";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

const DC_LICENSE_FAQ = {
  q: "Do I need a license to rent my home on Airbnb in Washington, D.C.?",
  a: "Yes. DC requires a short-term rental license from the Department of Licensing and Consumer Protection (DLCP). The home has to be your primary residence, which the District defines as a property that qualifies for the Homestead Deduction. It also has to be owned by an individual, not an LLC or corporation. A 'Short-Term Rental' license covers hosting while you live there, such as a spare room or English basement. A 'Vacation Rental' license covers renting the whole home while you're away, which DC limits to 90 nights a year unless you qualify for an exemption. Every stay is capped at 30 nights. We help you understand DC's license requirements, set up your listing with your license number displayed, and run bookings within the rules that apply to your home. We aren't attorneys and can't guarantee approval, so for anything specific to your situation, check with DLCP (dlcp.dc.gov) or your own legal or tax advisor.",
};

const FAQ = ({ includeDCLicense = false }: { includeDCLicense?: boolean }) => {
  const revealRef = useReveal<HTMLDivElement>();
  const faqs = [
    {
      q: "Is my property eligible for Airbnb?",
      a: "We will check zoning laws and specific permits for your area in VA & DC (Alexandria City, Arlington, Fairfax, Loudoun, Prince William, and Washington, D.C.) to ensure legal operation.",
    },
    {
      q: "I have no experience with Airbnb, can JNN Homes help?",
      a: "Absolutely! JNN Homes specializes in helping new hosts get started. We handle everything from furnishing and photography to daily operations. You just collect the income.",
    },
    {
      q: "Do I need to answer guest messages myself?",
      a: "No. The JNN Homes team is on call 24/7 to answer all guest inquiries within 5 minutes, from their initial question to check-out.",
    },
    {
      q: "What if guests damage the property?",
      a: "Airbnb provides AirCover insurance up to $1 million USD. JNN Homes will file claims and request compensation on your behalf if any incidents occur.",
    },
    {
      q: "I live in another state, how is it managed?",
      a: "This is exactly why JNN Homes exists. We are your 'eyes and ears' and 'hands' across VA & DC. You can track everything through our app and monthly reports.",
    },
    {
      q: "Can I just get information first without committing right away?",
      a: "You are very welcome to! Just submit your information, and we will do a free market analysis for you so you can get a comprehensive overview before deciding.",
    },
    {
      q: "Will running an Airbnb bother my neighbors?",
      a: "We have a strict guest screening process and install noise monitoring devices (NoiseAware) to ensure guests do not cause disturbances or throw parties.",
    },
    {
      q: "Who pays for minor repairs in the house?",
      a: "The homeowner covers the cost of materials, and JNN Homes has a maintenance team to quickly handle minor issues. We always consult you before undertaking major repairs.",
    },
    {
      q: "Why does JNN Homes need to analyze my house before managing it?",
      a: "We want to ensure that the Airbnb model will actually generate higher profits for you than a long-term rental. JNN Homes only takes on properties where we see real potential.",
    },
  ];

  if (includeDCLicense) {
    faqs.push(DC_LICENSE_FAQ);
  }

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqs.map(({ q, a }) => ({
      "@type": "Question",
      name: q,
      acceptedAnswer: { "@type": "Answer", text: a },
    })),
  };

  return (
    <section className="py-24 bg-muted/20">
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }}
      />
      <div ref={revealRef} className="container mx-auto px-4 max-w-4xl">
        <div className="text-center mb-16 space-y-4 reveal reveal-delay-1">
          <h2 className="text-4xl font-bold">Frequently Asked Questions</h2>
          <p className="text-muted-foreground italic">
            Everything you need to know about hosting with JNN Homes.
          </p>
        </div>

        <Accordion type="single" collapsible className="w-full space-y-4">
          {faqs.map((faq, i) => (
            <AccordionItem
              key={i}
              value={`item-${i}`}
              className={`reveal reveal-delay-${(i % 4) + 1} bg-card border border-border rounded-xl px-6`}
            >
              <AccordionTrigger className="text-left font-bold hover:no-underline py-6">
                {faq.q}
              </AccordionTrigger>
              <AccordionContent className="text-muted-foreground pb-6 leading-relaxed">
                {faq.a}
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </div>
    </section>
  );
};

export default FAQ;
